import { sample } from "util/sample";
import { Game } from "../types/Game";
import generateRandomNumberBetweenTwoNums from "../../../util/generateRandomNumberBetweenTwoNums";
import { Todo } from "shared/todos/Todo";
import { audioFileGainReductionLookup } from "../Looop";
import { isThisCompOnOptions } from "./config/comp/isThisCompOnOptions";
import { faderGraderOptions } from "./config/volume/faderGraderOptions";
import { canYouPanOptions } from "./config/pan/canYouPanOptions";
import { GameQuestion } from "../types/GameQuestions";
import { frequencyRangesMinMax } from "./config/eq/frequencyRangesMinMax";
import frequencyRangesByInstrument from "./config/eq/frequencyRangesByInstrument";

interface Params {
  todo: Todo;
  roundId: number;
}

export default function questionFromTodo({
  todo,
  roundId,
}: Params): GameQuestion {
  const { game, difficulty, looop, looop_instrument, id, mix_puzzle } = todo;
  const instrument = looop.instruments[looop_instrument];

  if (
    game === Game.freq_finder_boost ||
    game === Game.freq_finder_cut ||
    game === Game.freq_finder_hipass ||
    game === Game.freq_finder_lopass
  ) {
    const frequencyRange = sample(
      Array.from(frequencyRangesByInstrument[looop_instrument].frequencies)
    );

    return {
      roundId: `${roundId}`,
      type: game,
      difficulty,
      instrument,
      looop,
      frequency: generateRandomNumberBetweenTwoNums(
        frequencyRangesMinMax[frequencyRange]
      ),
      todoId: id,
    };
  }

  if (game === Game.is_this_compressor_on) {
    const selectedOption = sample(isThisCompOnOptions[difficulty]);
    const reversedOption = [selectedOption[1], selectedOption[0]] as const; // reverse so that b doesn't always have the higher gain reduction rate
    const [a, b] = sample([selectedOption, reversedOption]);
    const answer =
      audioFileGainReductionLookup[a] === audioFileGainReductionLookup[b]
        ? "neither"
        : audioFileGainReductionLookup[a] > audioFileGainReductionLookup[b]
        ? "a"
        : "b";

    return {
      type: game,
      difficulty,
      instrument,
      looop,
      roundId: `${roundId}`,
      answer,
      options: { a, b },
      todoId: id,
    };
  }

  if (game === Game.fader_grader) {
    const volume = sample(faderGraderOptions[difficulty].options);

    return {
      type: game,
      difficulty,
      instrument,
      looop,
      roundId: `${roundId}`,
      volume,
      maxVolume: faderGraderOptions[difficulty].max,
      minVolume: faderGraderOptions[difficulty].min,
      steps: faderGraderOptions[difficulty].steps,
      todoId: id,
    };
  }

  if (game === Game.can_you_pan) {
    const pan = sample(canYouPanOptions[difficulty].panOptions);

    return {
      type: game,
      difficulty,
      instrument,
      looop,
      roundId: `${roundId}`,
      panDegree: pan,
      panDegreeSteps: canYouPanOptions[difficulty].steps,
      todoId: id,
    };
  }

  // not desctructured for typing purposes
  if (todo.game === Game.mixPuzzle) {
    return {
      type: todo.game,
      mixPuzzle: todo.mix_puzzle,
      todoId: todo.id,
      difficulty: todo.difficulty,
      roundId: `${roundId}`,
    };
  }

  throw new Error(`Invalid game: ${game}`);
}
